import { useMemo, useState } from "react";
import { useCustom, type useTranslate } from "@refinedev/core";

import { bouwblokken, kiesBouwblok } from "./bouwblokken";

import {
  fieldsByGroup,
  fieldsFromServer,
  type ServerFieldCatalog,
  type TemplateField,
} from "./templateFieldCatalog";
import { CeIcon } from "./CeIcon";

// communicatie: the "Beschikbare velden" palette. Clicking a field inserts a variable PILL at the editor
// cursor (a `variable` ProseMirror node), which serializes to the literal {Group.Field} token inside its
// span — the round-trip contract the api/Jmix render pipeline depends on. The catalog is now SERVER-
// SOURCED via GET communicatie/templates/fields (the authoritative variable catalog); the static
// templateFieldCatalog is only an offline fallback when that fetch fails. The list is grouped
// (Relatie / Dier / Verblijf / …) with a free-text filter, mirroring the Jmix editor's side panel.

type Translate = ReturnType<typeof useTranslate>;

/**
 * Wat de AI terugstuurt op een vrije vraag.
 *
 * <p>Een voorstel wordt nooit vanzelf toegepast. De AI herschrijft het hele document, en dat is
 * precies het soort wijziging dat je wilt zien voordat hij erin staat -- zeker omdat een verdwenen
 * {Token} pas op papier bij de klant opvalt. De server controleert dat ook (placeholdersPreserved);
 * is dat niet goed, dan komt het voorstel hier als {@code ok: false} binnen en is toepassen geen optie.
 */
export type AiUitkomst =
  | { ok: true; samenvatting: string; waarschuwingen: string[]; toepassen: () => void }
  | { ok: false; melding: string };

/** Het icoon per veldgroep, zoals de bron ze toewijst (grpIcon in comm-editor.js). */
const GROEP_ICOON: Record<string, string> = {
  Relatie: "users",
  Dier: "paw2",
  Verblijf: "home",
  Factuur: "doc",
  Kosten: "doc",
  Vestiging: "building",
  Datum: "calendar",
};

/**
 * Het veld waar een vraag naar wijst, of {@code null}.
 *
 * <p>Alleen bij één duidelijke treffer. Twee velden die allebei passen ("naam" komt overal voor) is
 * geen antwoord maar een gok, en dan is doorsturen naar de AI eerlijker dan iets invoegen waar niet
 * om gevraagd is. Een exacte labeltreffer wint altijd van een deeltreffer: wie "opmerking" typt
 * bedoelt het veld Opmerking, ook al bevat "Interne opmerking" hetzelfde woord.
 */
function kiesVeld(vraag: string, groepen: { group: string; fields: TemplateField[] }[]): TemplateField | null {
  const tekst = vraag.toLowerCase();
  if (!tekst.trim()) return null;
  const alle = groepen.flatMap((g) => g.fields);
  const exact = alle.filter((f) => {
    const label = f.label.toLowerCase();
    return new RegExp(`(^|[^a-z])${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z]|$)`).test(tekst);
  });
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) {
    // Meerdere exacte treffers: de langste is de specifiekste ("interne opmerking" boven "opmerking").
    const langste = exact.reduce((a, b) => (b.label.length > a.label.length ? b : a));
    if (exact.filter((f) => f.label.length === langste.label.length).length === 1) return langste;
  }
  return null;
}

export function VariablePalette({
  onInsert,
  onInsertHtml,
  onAiVraag,
  t,
  disabled,
}: {
  onInsert: (token: string) => void;
  /** Een heel blok invoegen in plaats van één veld. Zonder dit blijft de hulpbalk weg. */
  onInsertHtml?: (html: string) => void;
  /** Alles wat geen vast bouwblok is: de vraag naar de AI, die een voorstel teruggeeft. */
  onAiVraag?: (vraag: string) => Promise<AiUitkomst>;
  t: Translate;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [vraag, setVraag] = useState("");
  const [hulpMelding, setHulpMelding] = useState("");
  const [aiBezig, setAiBezig] = useState(false);
  const [voorstel, setVoorstel] = useState<Extract<AiUitkomst, { ok: true }> | null>(null);

  // RELATIVE url — the dataProvider prepends /api once. Viewer-readable (auth: viewer).
  const { data, isLoading, isError } = useCustom<ServerFieldCatalog>({
    url: "communicatie/templates/fields",
    method: "get",
  });

  // Server is the source of truth; fall back to the bundled static catalog on error/empty so the palette
  // is never blank for an editor.
  const baseGroups = useMemo<{ group: string; fields: TemplateField[] }[]>(() => {
    const fromServer = fieldsFromServer(data?.data);
    if (fromServer.length > 0) return fromServer;
    if (isError) return fieldsByGroup();
    return [];
  }, [data, isError]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return baseGroups
      .map((g) => ({
        group: g.group,
        fields: q
          ? g.fields.filter(
              (f) =>
                f.token.toLowerCase().includes(q) ||
                f.label.toLowerCase().includes(q) ||
                f.group.toLowerCase().includes(q),
            )
          : g.fields,
      }))
      .filter((g) => g.fields.length > 0);
  }, [baseGroups, query]);

  // De blokken kennen alleen de velden die deze installatie ook echt heeft; een kolom waarvan het
  // veld ontbreekt hoort er niet in te staan.
  const blokken = useMemo(() => {
    const tokens = new Set(baseGroups.flatMap((g) => g.fields.map((f) => f.token)));
    return bouwblokken(tokens);
  }, [baseGroups]);

  /**
   * Eerst de vaste bouwblokken, dan pas de AI.
   *
   * <p>Een factuurregel heeft precies één goede vorm en die staat in code; die hoeft niemand te laten
   * verzinnen, en zo gaat het ook meteen in plaats van na een seconde wachten. Alles daarbuiten --
   * "haal de kolom aantal weg", "zet er een blok met het chipnummer onder" -- is wel werk voor de AI,
   * en dat komt terug als voorstel dat je eerst ziet.
   */
  const vraagHulp = async () => {
    // Eerst: wijst de vraag naar één veld uit de catalogus? Dan is dat het antwoord, en hoeft er
    // niets herschreven te worden. Dit stond er eerst niet, en dan ging "zet de opmerking erbij"
    // naar de AI -- die herschrijft het héle document, raakt op een dicht afdruksjabloon altijd wel
    // een {token} kwijt, en dan blokkeert de veiligheidsregel het voorstel. Terecht geblokkeerd,
    // maar onbruikbaar: je vroeg om één veld.
    const veld = kiesVeld(vraag, baseGroups);
    if (veld) {
      onInsert(veld.token);
      setHulpMelding(t("communicatieEditor.hulp.veldIngevoegd", { args: [veld.label] }));
      setVoorstel(null);
      setVraag("");
      return;
    }
    const blok = kiesBouwblok(vraag, blokken);
    if (blok && onInsertHtml) {
      onInsertHtml(blok.html());
      setHulpMelding(t("communicatieEditor.hulp.ingevoegd", { args: [blok.naam] }));
      setVoorstel(null);
      setVraag("");
      return;
    }
    if (!onAiVraag) {
      setHulpMelding(t("communicatieEditor.hulp.onbekend"));
      return;
    }
    setAiBezig(true);
    setHulpMelding("");
    setVoorstel(null);
    try {
      const uit = await onAiVraag(vraag);
      if (uit.ok) setVoorstel(uit);
      else setHulpMelding(uit.melding);
    } finally {
      setAiBezig(false);
    }
  };

  const loading = isLoading && baseGroups.length === 0;

  // Zoeken klapt alles open: anders typ je een woord en gebeurt er zichtbaar niets omdat de treffer
  // in een dichte groep zit.
  const zoekt = query.trim().length > 0;

  return (
    <div className="ce-velden" aria-label={t("communicatieEditor.palette.title")}>
      {/* Zelf een factuurregel opbouwen vraagt kennis van het sjabloonformaat: een herhaalblok, de
          juiste rijklassen, en de tokens erbinnen. Hier typ je wat je wilt en komt het goede blok
          eruit. Wat er ingevoegd wordt komt uit vaste bouwstenen -- zie bouwblokken.ts waarom. */}
      {onInsertHtml && (
        <div className="ce-hulp">
          <label className="ce-hulp-kop" htmlFor="ce-hulp-vraag">
            <CeIcon naam="variable" />
            {t("communicatieEditor.hulp.titel")}
          </label>
          <div className="ce-hulp-rij">
            <input
              id="ce-hulp-vraag"
              type="text"
              value={vraag}
              placeholder={t("communicatieEditor.hulp.voorbeeld")}
              disabled={disabled}
              onChange={(e) => { setVraag(e.target.value); setHulpMelding(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); vraagHulp(); } }}
            />
            <button type="button" disabled={disabled || aiBezig || !vraag.trim()}
              onMouseDown={(e) => e.preventDefault()} onClick={() => { void vraagHulp(); }}>
              {t(aiBezig ? "communicatieEditor.hulp.bezig" : "communicatieEditor.hulp.invoegen")}
            </button>
          </div>
          {hulpMelding && <p className="ce-hulp-melding">{hulpMelding}</p>}

          {/* Het voorstel van de AI, voordat het in het document staat. */}
          {voorstel && (
            <div className="ce-hulp-voorstel">
              <p>{voorstel.samenvatting}</p>
              {voorstel.waarschuwingen.length > 0 && (
                <ul>{voorstel.waarschuwingen.map((w, i) => <li key={i}>{w}</li>)}</ul>
              )}
              <div className="ce-hulp-voorstel-knoppen">
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { voorstel.toepassen(); setVoorstel(null); setVraag(""); setHulpMelding(t("communicatieEditor.hulp.toegepast")); }}>
                  {t("communicatieEditor.hulp.toepassen")}
                </button>
                <button type="button" className="ce-hulp-weg" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setVoorstel(null)}>
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
          <div className="ce-hulp-blokken">
            {blokken.map((b) => (
              <button key={b.sleutel} type="button" className="ce-hulp-blok" title={b.uitleg}
                disabled={disabled} onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onInsertHtml(b.html()); setHulpMelding(t("communicatieEditor.hulp.ingevoegd", { args: [b.naam] })); }}>
                {b.naam}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ce-rsearch">
        <CeIcon naam="search" />
        <input
          type="search"
          autoComplete="off"
          value={query}
          placeholder={t("communicatieEditor.palette.search")}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t("communicatieEditor.palette.search")}
        />
      </div>

      {loading && <p className="comm-editor__palette-empty">{t("common.loading")}</p>}
      {!loading && groups.length === 0 && (
        <p className="comm-editor__palette-empty">{t("communicatieEditor.palette.empty")}</p>
      )}

      {groups.map((g) => (
        <details key={g.group} className="ce-grp" open={zoekt || g.group === groups[0]?.group}>
          <summary className="ce-grp-h">
            <span className="ce-grp-ic">
              <CeIcon naam={GROEP_ICOON[g.group] ?? "doc"} />
            </span>
            <span className="ce-grp-name">{g.group}</span>
            <span className="ce-grp-count">{g.fields.length}</span>
            <span className="ce-grp-chev">
              <CeIcon naam="chevright" />
            </span>
          </summary>
          <div className="ce-grp-items">
            {g.fields.map((f) => (
              <button
                key={f.token}
                type="button"
                className="ce-fld"
                title={f.description ? `{${f.token}} — ${f.description}` : `{${f.token}}`}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onInsert(f.token)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}
